import React, { useState } from "react";
import { IoCheckmark, IoChevronDown, IoClose } from "react-icons/io5";
import SettingsInfoTip from "./SettingsInfoTip";
import CommaTagInput from "./CommaTagInput";
import {
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  splitCommaValues,
} from "./businessFilterPayload";
import {
  buildKeywordFilterChips,
  buildSenderFilterChips,
} from "./businessDigestSetup";
import { getSenderChipColor } from "./gmailFilterChips";

/**
 * Inline label with a hover/focus popover listing filter values.
 *
 * @example
 * <GmailFilterHoverTerm label="meeting summary emails" items={["Zoom", "Google Calendar", "Google Meet"]} />
 */
const GmailFilterHoverTerm = ({ label, items, variant = "sender" }) => (
  <span className="gf-hover-term">
    <button type="button" className="gf-hover-term__trigger" aria-haspopup="true">
      {label}
    </button>
    <span className="gf-hover-term__popover" role="tooltip">
      {variant === "sender" ? (
        <span className="gf-hover-term__list">
          {items.map((platform) => (
            <span key={platform} className="gf-hover-term__item">
              <span
                className="gf-hover-term__dot"
                style={{ background: getSenderChipColor(platform) }}
                aria-hidden="true"
              />
              {platform}
            </span>
          ))}
        </span>
      ) : (
        <span className="gf-hover-term__list gf-hover-term__list--keywords">
          {items.map((keyword) => (
            <span key={keyword} className="gf-hover-term__item gf-hover-term__item--keyword">
              {keyword}
            </span>
          ))}
        </span>
      )}
    </span>
  </span>
);

/**
 * Gmail fetch filter summary with collapsible sender/keyword customization.
 *
 * @example
 * <GmailFilterSetupCard
 *   senderPatterns="no-reply@zoom.us"
 *   subjectKeywords="meeting, agenda"
 *   onSenderPatternsChange={setSenders}
 *   onSubjectKeywordsChange={setKeywords}
 * />
 */
const GmailFilterSetupCard = ({
  senderPatterns = MEETING_DEFAULT_SENDERS,
  subjectKeywords = MEETING_DEFAULT_KEYWORDS,
  onSenderPatternsChange,
  onSubjectKeywordsChange,
}) => {
  const [showCustomize, setShowCustomize] = useState(false);
  const meetingRule = { senderPatterns, subjectKeywords };
  const senderChips = buildSenderFilterChips(meetingRule);
  const keywordChips = buildKeywordFilterChips(meetingRule);

  return (
    <div className="gf-card">
      <p className="gf-summary">
        Pulls action items from{" "}
        <GmailFilterHoverTerm
          label="meeting summary emails"
          items={senderChips}
          variant="sender"
        />
        {" that contain "}
        <GmailFilterHoverTerm label="meeting keywords" items={keywordChips} variant="keyword" />
        {" — summaries only, never your full inbox."}
      </p>

      <div className="gf-foot">
        <div className="gf-foot__line">
          <span className="gf-foot__icon gf-foot__icon--yes" aria-hidden="true">
            <IoCheckmark />
          </span>
          Calendar invites are always included.
        </div>
        <div className="gf-foot__line">
          <span className="gf-foot__icon gf-foot__icon--no" aria-hidden="true">
            <IoClose />
          </span>
          Everything else is never accessed.
        </div>
      </div>

      <div className="gf-custom">
        <button
          type="button"
          className="gf-custom__toggle"
          aria-expanded={showCustomize}
          onClick={() => setShowCustomize((open) => !open)}
        >
          <IoChevronDown
            className={`gf-custom__chevron${showCustomize ? " gf-custom__chevron--open" : ""}`}
            aria-hidden="true"
          />
          Customize meeting summary email senders &amp; keywords
        </button>
        {showCustomize ? (
          <div className="gf-custom__body">
            <div className="settings-general-block settings-general-block--nested">
              <div className="settings-general-block__heading-row">
                <h4 className="settings-general-block__subtitle">Meeting summary email senders</h4>
                <SettingsInfoTip
                  label="About meeting summary email senders"
                  text="Senders of meeting summary emails. Press Enter to add."
                />
              </div>
              <CommaTagInput
                value={senderPatterns}
                onChange={onSenderPatternsChange}
                placeholder="+ Add sender…"
              />
            </div>
            <div className="settings-general-block settings-general-block--nested">
              <div className="settings-general-block__heading-row">
                <h4 className="settings-general-block__subtitle">
                  And emails containing the following keywords in the subject
                </h4>
                <SettingsInfoTip
                  label="About meeting keywords"
                  text="Keywords that must appear in the subject line. Press Enter to add."
                />
              </div>
              <CommaTagInput
                value={subjectKeywords}
                onChange={onSubjectKeywordsChange}
                placeholder="+ Add keyword…"
              />
            </div>
            {splitCommaValues(senderPatterns).length === 0 ? (
              <p className="gf-custom__hint">Add at least one meeting summary email sender before finishing setup.</p>
            ) : null}
            {splitCommaValues(subjectKeywords).length === 0 ? (
              <p className="gf-custom__hint">Add at least one meeting keyword before finishing setup.</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GmailFilterSetupCard;
